import { useEffect, useState } from "react";
import { useManagedCompanies } from "@/auth/useManagedCompanies";
import { publicOnboardingAllowed } from "@/business-onboarding/eligibility";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { BusinessOnboarding } from "@/business-onboarding/BusinessOnboarding";
import { resetJourneyForAdmin } from "@/business-onboarding/storage";
import { useOnboardingConfiguration } from "@/business-onboarding/configuration";
import { pageTitle } from "@/lib/brand";

export const Route = createFileRoute("/business-onboarding")({
  validateSearch: (search: Record<string, unknown>): { adminTest?: "new" } =>
    search["adminTest"] === "new" ? { adminTest: "new" } : {},
  beforeLoad: ({ search, preload }) => {
    if (!preload) void useOnboardingConfiguration.getState().load();
    if (!preload && search.adminTest === "new") {
      resetJourneyForAdmin();
      // Consume the test-only signal before rendering, so refresh/back cannot repeat it.
      throw redirect({ to: "/business-onboarding", search: {}, replace: true });
    }
  },
  head: () => ({
    meta: [
      { title: pageTitle("Explore Qotta for your business") },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PublicBusinessOnboarding,
});

// Keep compatibility without letting direct entry bypass public launch eligibility.
function PublicBusinessOnboarding() {
  const account = useManagedCompanies();
  const allowed = window.parent === window && publicOnboardingAllowed(account);
  const pending = account.resolving || (!account.adminError && !account.membershipError && account.companies === null);
  const [entered, setEntered] = useState(allowed);
  useEffect(() => { if (allowed) setEntered(true); }, [allowed]);
  if (!allowed && !pending) return <main className="grid min-h-screen place-content-center gap-5 p-6">
    <p role="status">Business signup is unavailable for this account.</p>
    <Link to="/" search={{ view: "public" }} className="underline">Return to Qotta homepage</Link>
  </main>;
  return <>
    {entered && <div hidden={!allowed} inert={!allowed ? true : undefined}><BusinessOnboarding /></div>}
    {pending && <p role="status" className="p-8">Checking account...</p>}
  </>;
}
